import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";
import { useAtomValue } from "jotai";

import Module from "@/components/module/Module";
import { Stock } from "@/models/stock";
import { StockEPSMomentum } from "@/models/stock-eps";
import StockEPSHistoryChart from "../stock-eps-history-chart/StockEPSHistoryChart";
import EpsHorizons, { changeOver, type Point } from "./EpsHorizons";

import { getStockEPSMomentum } from "@/controllers/stock-eps";
import { negativeState, positiveState } from "@/lib/store";
import { priceDiffPercentage } from "@/lib/utils";

export const getPercentageChange = (timeSeries: [number, number][] | null | undefined, days: number, shortened = false) => {
    // No series (a visitor, or a stock without estimates) means no change to show.
    if (!Array.isArray(timeSeries) || timeSeries.length === 0) return null;
    const lastDay = timeSeries[timeSeries.length - 1];
    const daysAgo = dayjs(shortened ? lastDay[0] : undefined).subtract(days, "days");
    const filteredDays = timeSeries.filter((item) => dayjs(item[0]).isAfter(daysAgo, "days"));

    if (!filteredDays.length) return null;

    const firstDay = filteredDays[0];

    return priceDiffPercentage(firstDay[1], lastDay[1]);
};

// A consensus older than this has stopped updating, so the page says so rather than reading as current.
const STALE_AFTER_DAYS = 7;

const signed = (value: number | null | undefined, suffix: string) => (value == null ? "N/A" : `${value > 0 ? "+" : ""}${value.toFixed(2)}${suffix}`);

/** One sentence on how the estimate moved against the share price over the last month. */
const readMomentum = (eps: number, price: number): string => {
    if (eps === 0 && price === 0) return "Estimates and the share price are flat";
    if (eps === 0) return `Estimates are flat while the share price ${price > 0 ? "rises" : "falls"}`;
    if (price === 0) return `The share price is flat while estimates ${eps > 0 ? "rise" : "fall"}`;
    if (eps > 0 && price > 0) return eps >= price ? "Estimates are rising faster than the share price" : "The share price is rising faster than estimates";
    if (eps > 0) return "Estimates are rising while the share price falls";
    if (price > 0) return "The share price is rising while estimates fall";
    return eps >= price ? "Estimates are falling more slowly than the share price" : "Estimates are falling faster than the share price";
};

const StatTile: FC<{ label: string; value: string; color?: string; hint?: string; obfuscated?: boolean }> = ({ label, value, color, hint, obfuscated }) => (
    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 96 }}>
        <Typography className={obfuscated ? "obfuscated" : undefined} sx={{ fontSize: "1.35rem", fontWeight: 700, lineHeight: 1.1, color }}>
            {value}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {label}
            </Typography>
            {hint && (
                <Tooltip title={hint}>
                    <InfoOutlinedIcon sx={{ fontSize: 12, color: "text.secondary" }} />
                </Tooltip>
            )}
        </Box>
    </Box>
);

const StockEps = ({ stock }: { stock: Stock }) => {
    const positive = useAtomValue(positiveState);
    const negative = useAtomValue(negativeState);

    const [momentum, setMomentum] = useState<StockEPSMomentum>();
    const [eps, setEps] = useState<Point[] | null>(null);
    const [price, setPrice] = useState<Point[]>([]);
    const [shortened, setShortened] = useState(false);

    useEffect(() => {
        if (!stock.stock_id) return;
        getStockEPSMomentum(stock.stock_id).then(setMomentum);
    }, [stock.stock_id]);

    const onChartData = useCallback((epsSeries: Point[], priceSeries: Point[], delayed: boolean) => {
        setEps(epsSeries);
        setPrice(priceSeries);
        setShortened(delayed);
    }, []);

    const obfuscated = !!momentum?.obfuscated;
    const epsChange = momentum?.["EPS change"] ?? null;
    const priceChange = momentum?.["Price change"] ?? null;
    const divergence = momentum?.["EPS/Price divergence"] ?? null;

    const latestEps = eps?.length ? eps[eps.length - 1] : null;
    const latestPrice = price.length ? price[price.length - 1][1] : null;
    const forwardPe = latestEps && latestPrice && latestEps[1] > 0 ? latestPrice / latestEps[1] : null;
    const ageInDays = latestEps ? dayjs().diff(dayjs(latestEps[0]), "day") : 0;
    // A delayed plan sees an old newest point by design, which is not a stalled consensus.
    const stale = !shortened && ageInDays > STALE_AFTER_DAYS;
    const noConsensus = eps !== null && eps.length === 0;

    const tone = (value: number | null) => (value == null || obfuscated ? undefined : value < 0 ? negative : positive);

    const headline = useMemo(() => {
        if (noConsensus) return `No EPS consensus for ${stock.name}`;
        if (stale && latestEps) return `The EPS consensus last updated on ${dayjs(latestEps[0]).format("D MMM YYYY")}`;
        if (obfuscated || epsChange == null || priceChange == null) return `${stock.name} EPS momentum`;
        return readMomentum(epsChange, priceChange);
    }, [noConsensus, stale, latestEps, obfuscated, epsChange, priceChange, stock.name]);

    const caption = noConsensus
        ? "ORTEX tracks the 12-month forward estimate for stocks with at least three analyst recommendations, so this stock has no estimate history here."
        : "EPS is the analyst consensus for the next 12 months. The gap is the EPS change minus the share price change, so a positive gap means estimates are moving ahead of the price.";

    return (
        <Box
            sx={{
                display: "grid",
                gap: "10px",
                gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: noConsensus ? "minmax(0, 1fr)" : "minmax(0, 2fr) minmax(0, 1fr)" },
                gridTemplateRows: { xs: "auto 360px auto", lg: "auto minmax(0, 1fr)" },
                height: { lg: "100%" },
            }}
        >
            <Box sx={{ gridColumn: "1 / -1", minWidth: 0 }}>
                <Module noHeader>
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: { xs: 2, lg: 4 }, alignItems: { lg: "center" }, p: 1 }}>
                        <Box sx={{ flex: "1 1 auto", minWidth: 0, maxWidth: { lg: 640 } }}>
                            <Typography component="h2" sx={{ fontWeight: 700, fontSize: "1rem" }}>
                                {headline}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", lineHeight: 1.5 }}>
                                {caption}
                            </Typography>
                        </Box>
                        {!noConsensus && (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 2, md: 3 }, flex: "0 0 auto", ml: { lg: "auto" } }}>
                                <StatTile label="EPS 12m fwd" value={latestEps ? latestEps[1].toFixed(2) : "N/A"} obfuscated={obfuscated} />
                                <StatTile label="EPS 1m" value={signed(epsChange, "%")} color={tone(epsChange)} obfuscated={obfuscated} />
                                <StatTile label="Price 1m" value={signed(priceChange, "%")} color={tone(priceChange)} obfuscated={obfuscated} />
                                <StatTile label="Gap 1m" value={signed(divergence, "pp")} color={tone(divergence)} obfuscated={obfuscated} hint="EPS change minus share price change, in percentage points." />
                                {forwardPe != null && <StatTile label="Forward P/E" value={`${forwardPe.toFixed(1)}x`} obfuscated={obfuscated} hint="Latest share price over the 12-month forward EPS estimate." />}
                            </Box>
                        )}
                    </Box>
                </Module>
            </Box>

            <Box sx={{ minWidth: 0, minHeight: 0, display: "flex" }}>
                <Module title={noConsensus ? "Share price" : "EPS estimate and share price"} help={{ moduleId: "eps_chart", modulePage: "V2_stock_eps" }} feedback noPadding={{ source: "body", type: "mobile" }}>
                    <StockEPSHistoryChart stock={stock} onData={onChartData} />
                </Module>
            </Box>

            {!noConsensus && (
                <Box sx={{ minWidth: 0, minHeight: 0, display: "flex" }}>
                    <Module title="EPS against price" help={{ moduleId: "eps", modulePage: "V2_stock_eps" }}>
                        <EpsHorizons
                            eps={eps ?? []}
                            price={price}
                            obfuscated={obfuscated}
                            // The month row uses the server figures so it agrees with the tiles above.
                            month={{ eps: epsChange ?? (eps ? changeOver(eps, 30) : null), price: priceChange ?? changeOver(price, 30) }}
                        />
                    </Module>
                </Box>
            )}
        </Box>
    );
};

export default StockEps;
